import {
  DeviceResult,
  deviceFail,
  deviceOk,
  isBrowser,
} from "./types";

/**
 * Captura de fotos: justificaciones de marca (§8.3.6), evidencia en formularios de
 * actividad (§8.6) y verificaciones nocturnas (§8.4).
 *
 * Se comprime **siempre antes de encolar**. Una foto de 4 MB desde una conexión de
 * campo no sube: bloquea la cola y consume el plan de datos del empleado (§10.3).
 */

export type PhotoOptions = {
  /** Abre la cámara directamente en móvil; si es `false`, la galería. */
  preferCamera?: boolean;
  /** Lado mayor en píxeles. 1600 basta para leer un documento o ver una habitación. */
  maxDimension?: number;
  maxSizeMb?: number;
};

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_MAX_SIZE_MB = 0.6;

export function isCameraSupported(): boolean {
  return isBrowser() && typeof document !== "undefined";
}

/**
 * Abre el selector nativo. Este es exactamente el punto que Capacitor sustituye por
 * `Camera.getPhoto()` (§3.3); por eso la elección de archivo vive en la capa de
 * dispositivo y no dentro de un componente con un `<input>` escondido.
 */
export function pickPhoto(
  options: PhotoOptions = {},
): Promise<DeviceResult<File>> {
  if (!isCameraSupported()) {
    return Promise.resolve(
      deviceFail<File>("unsupported", "Este dispositivo no permite tomar fotos."),
    );
  }

  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    if (options.preferCamera !== false) input.capture = "environment";
    input.style.display = "none";
    document.body.appendChild(input);

    let settled = false;
    const finish = (result: DeviceResult<File>) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(result);
    };

    input.addEventListener("change", () => {
      const file = input.files?.[0];
      finish(
        file
          ? deviceOk(file)
          : deviceFail<File>("cancelled", "No se eligió ninguna foto."),
      );
    });

    // Navegadores modernos avisan cuando el usuario cierra el selector. En los que
    // no lo emiten, la promesa queda pendiente hasta que se elija algo — aceptable:
    // no bloquea la interfaz, solo no resuelve.
    input.addEventListener("cancel", () =>
      finish(deviceFail<File>("cancelled", "No se eligió ninguna foto.")),
    );

    input.click();
  });
}

/**
 * `browser-image-compression` (ya en el proyecto) se carga bajo demanda: pesa lo
 * suyo y la mayoría de las sesiones nunca adjunta una foto.
 */
export async function compressPhoto(
  file: File,
  options: PhotoOptions = {},
): Promise<DeviceResult<File>> {
  try {
    const { default: imageCompression } = await import(
      "browser-image-compression"
    );

    const compressed = await imageCompression(file, {
      maxSizeMB: options.maxSizeMb ?? DEFAULT_MAX_SIZE_MB,
      maxWidthOrHeight: options.maxDimension ?? DEFAULT_MAX_DIMENSION,
      useWebWorker: true,
    });

    return deviceOk(
      new File([compressed], file.name, {
        type: compressed.type || file.type,
        lastModified: Date.now(),
      }),
    );
  } catch (error) {
    return deviceFail(
      "unavailable",
      error instanceof Error ? error.message : "No se pudo procesar la foto.",
    );
  }
}

/** Elegir y comprimir en un paso: lo que usan las pantallas. */
export async function capturePhoto(
  options: PhotoOptions = {},
): Promise<DeviceResult<File>> {
  const picked = await pickPhoto(options);
  if (!picked.ok) return picked;
  return compressPhoto(picked.value, options);
}

/**
 * IndexedDB guarda `Blob` sin problema, pero un `Blob` no sobrevive a `JSON.stringify`
 * ni a un envío que espere texto. Para dejar una foto en la cola se convierte a data
 * URL, que sí viaja entera dentro del `payload`.
 */
export function photoToDataUrl(file: File): Promise<DeviceResult<string>> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(
        typeof reader.result === "string"
          ? deviceOk(reader.result)
          : deviceFail<string>("unavailable", "No se pudo leer la foto."),
      );
    reader.onerror = () =>
      resolve(deviceFail<string>("unavailable", "No se pudo leer la foto."));
    reader.readAsDataURL(file);
  });
}
